<template>
  <div>
    <div
      class="errorDialogue"
      v-if="errors.length">
      <div class="alert alert-danger">
        <button
          class="btn btn-clear-error"
          @click="clearErrors">
          <i class="fas fa-times"></i>
        </button>
        <p>มีข้อผิดพลาดเกิดขึ้น</p>
        <ul
          v-for="(error, errorIndex) in errors"
          :key="errorIndex">
          <li v-html="error"></li>
        </ul>
        <button
          class="btn btn-danger"
          v-if="hasFailedUpload"
          @click="retryAll">
          <i class="fas fa-redo-alt"></i> อัปโหลดไฟล์ที่มีปัญหาทั้งหมดอีกครั้ง
        </button>
        <button
          class="btn btn-danger"
          v-if="hasFailedUpload"
          @click.stop="deleteAllFailedImages">
          <i class="fas fa-trash"></i> ลบไฟล์ที่มีปัญหาทั้งหมด
        </button>
      </div>
    </div>
    <div
      class="infoDialogue"
      v-if="images.length > 0 && !allImagesUploaded && !errors.length">
      <div class="alert alert-secondary">
        <p>กำลังอัปโหลดภาพ ({{uploadSuccessful}}/{{images.length}})</p>
        <div :class="`progress ${allImagesUploaded ? 'hide' : ''}`">
          <div
            class="loader"
            :style="{width: `${parseInt(Math.round((uploadSuccessful / images.length) * 100), 10)}%`}">
          </div>
          <div class="glowing"></div>
        </div>
        <button
          class="btn btn-secondary"
          v-if="images.length > 0 && !allImagesUploaded"
          @click="abortAll">
          <i class="far fa-stop-circle"></i> หยุดรายการที่กำลังอัปโหลดทั้งหมด
        </button>
      </div>
    </div>
    <div
      class="uploader"
      v-if="!failedToGetOrders"
      @dragenter="OnDragEnter"
      @dragleave="OnDragLeave"
      @dragover.prevent
      @drop="onDrop">
      <div
        class="upload-control"
        v-if="hasImage">
        <label
          for="file"
          class="btn btn-primary">
          เพิ่มไฟล์
        </label>
      </div>
      <div v-show="!hasImage">
        <i class="fa fa-cloud-upload"></i>
        <p>ลากรูปภาพวางลงที่นี่</p>
        <div>หรือ</div>
        <div class="file-input">
          <label for="file">
            เลือกไฟล์
          </label>
          <input
            type="file"
            id="file"
            name="file"
            ref="file"
            @change="onInputChange"
            multiple>
        </div>
      </div>
      <draggable
        ghost-class="ghost"
        class="images-preview"
        v-model="images"
        v-if="hasImage"
        handle=".handle"
        @end="reorder"
        @click="deleteConfirm = false">
        <div
          class="img-wrapper"
          v-for="(image, index) in images"
          :id="'img'+index"
          :key="index">
          <div class="image-wrapper-actions">
            <button
              class="btn-set-cover"
              v-if="(!image.id && image.success && image.image) || image.id"
              @click="setCoverImage(image)">
              <i class="fas fa-star"></i>
              <span>ตั้งเป็นภาพปก</span>
            </button>
            <button
              class="btn-delete"
              @click="deleteImage(index)"
              v-if="deleteConfirm !== index">
              <i class="fas fa-times"></i>
            </button>
            <button
              class="btn-delete-confirm"
              @click="deleteImage(index, image.id)"
              v-if="deleteConfirm === index">
              <i class="fas fa-trash"></i> ยืนยันการลบ
            </button>
          </div>
          <div class="display-image handle">
            <div
              class="img-container"
              @click.stop="deleteConfirm = false"
              :style="{backgroundImage: `url(${image.image ? image.image.urls.content640w : ''})`}">
            </div>
            <div :class="`loading-container ${image.error ? 'upload-failed' : ''}`"></div>
          </div>
          <div
            v-if="Object.hasOwnProperty.call(image, 'isUploading')"
            :class="`progress ${image.uploadPercentage === 100 ? 'hide' : ''}`">
            <div
              class="loader"
              :style="{width: `${image.uploadPercentage}%`}">
            </div>
            <div class="glowing"></div>
          </div>
          <div class="details">
            <!-- Image Name -->
            <label :for="'name'+index">ชื่อภาพ</label>
            <input
              class="name"
              :id="'name'+index"
              :disabled="!image.id && !image.success"
              @change="setCurrentGalleryModifiedItem(image)"
              v-model="image.title">
            <!-- Image Description -->
            <label
              v-if="(!image.id && image.success) || image.id"
              :for="'desc'+index">
              คำอธิบายภาพ
            </label>
            <textarea
              :id="'desc'+index"
              class="desc"
              v-if="(!image.id && image.success) || image.id"
              @change="setCurrentGalleryModifiedItem(image)"
              v-model="image.description">
            </textarea>
            <button
              v-if="image.isUploading && !image.error"
              class="btn btn-secondary btn-retryUpload"
              @click.stop="abortUpload(image.identifier, index)">
              <i class="far fa-stop-circle"></i> หยุดการอัปโหลด
            </button>
            <button
              v-if="image.error"
              class="btn btn-danger btn-retryUpload"
              @click.stop="retryUpload(image.identifier)">
              <i class="fas fa-redo-alt"></i> อัปโหลดอีกครั้ง
            </button>
          </div>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import draggable from 'vuedraggable';
import dateFormatter from '@/utils/dateFormatter';
import { axiosImageAPI, isCancel } from '@/axios';
import { batchCreate } from '../api';

export default {
  mixins: [dateFormatter],
  components: {
    draggable,
  },
  data() {
    return {
      images: [],
      maxFileSizeInMB: 20,
      isDragging: false,
      dragCount: 0,
      uploadSuccessful: 0,
      isImageValid: false,
      deleteConfirm: false,
      failedToGetOrders: false,
      hasFailedUpload: false,
      request: null,
      isEditingGallery: false,
      requests: [],
      errors: [],
      failedToUpload: [],
    };
  },
  computed: {
    ...mapGetters([
      'currentGallery',
      'currentGalleryWasEdited',
    ]),
    hasImage() {
      return this.images.length > 0;
    },
    maxFileSizeInBytes() {
      return this.maxFileSizeInMB * 1024 * 2014;
    },
    allImagesUploaded() {
      return this.uploadSuccessful === this.images.length;
    },
  },
  created() {
    if (!_.isEmpty(this.currentGallery.itemIds) && !this.currentGallery.itemIds.length) {
      this.pushCurrentGalleryError('ไม่สามารถโหลดการเรียงลำดับของภาพได้');
      return;
    }
    // Load photos into the uploader area with order.
    this.images = _.sortBy(this.currentGallery.items, (item) => {
      return this.currentGallery.itemIds.indexOf(item.id);
    });
    this.uploadSuccessful = this.images.length;
  },
  methods: {
    ...mapActions([
      'pushCurrentGalleryError',
      'setCurrentGalleryWasEdited',
      'setCurrentGalleryModifiedItem',
      'setCurrentGalleryWasReordered',
      'setCurrentGallery',
      'resetCurrentGalleryErrors',
      'pushCurrentGalleryError',
      'pushCurrentGalleryToBeDeleted',
    ]),
    OnDragEnter(e) {
      e.preventDefault();
      this.dragCount += 1;
      this.isDragging = true;
      return false;
    },
    OnDragLeave(e) {
      e.preventDefault();
      this.dragCount -= 1;
      if (this.dragCount <= 0) {
        this.isDragging = false;
      }
    },
    onInputChange(e) {
      const files = Array.from(e.target.files);
      files.forEach((file) => this.pushAndUploadImage(file));
    },
    onDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = false;
      const files = Array.from(e.dataTransfer.files);
      files.forEach((file) => this.pushAndUploadImage(file));
    },
    isValidImage(file) {
      if (!file.type.match('image.*')) {
        this.errors.push(`ไฟล์ ${file.name} ไม่ใช่ไฟล์ภาพ (.jpg, .jpeg, .gif, .png)`);
        return false;
      }
      if (file.size > this.maxFileSizeInBytes) {
        this.errors.push(`ไฟล์ ${file.name} มีขนาดใหญ่กว่า 20 MB`);
        return false;
      }
      return true;
    },
    pushAndUploadImage(file) {
      if (this.request) this.abortUpload();
      if (!this.isValidImage(file)) return;

      const fileName = file.name;
      const [fileTitle] = fileName.split('.');
      const identifier = `${Math.floor(Math.random() * 100)}-${fileTitle}`;
      const formData = new FormData();
      formData.append('file', file);
      const imageData = {
        title: fileTitle,
        description: '',
        image: '',
        uploadPercentage: 0,
        success: false,
        isUploading: true,
        identifier,
        file,
      };
      this.images.push(imageData);
      const requestId = identifier;
      const self = this;
      axiosImageAPI
        .post('/assets/gallery-images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          requestId,
          onUploadProgress(progressEvent) {
            imageData.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100), 10,
            ) - 30;
          },
          onDownloadProgress() {
            imageData.uploadPercentage += 30;
          },
        })
        .then((response) => {
          self.uploadSuccessful += 1;
          imageData.isUploading = false;
          imageData.success = true;
          imageData.image = response.data;
          batchCreate(self.currentGallery.id, { items: [imageData] })
            .then((result) => {
              const newItems = result.data.items;
              const newItemId = newItems[0].id;
              const newGallery = {
                ...self.currentGallery,
                items: [...self.currentGallery.items, ...newItems],
                itemIds: [...self.currentGallery.itemIds, newItemId],
              };
              imageData.id = newItemId;
              self.$store.dispatch('setCurrentGallery', newGallery);
            }).catch((error) => {
              self.errors.push(error);
            });
        })
        .catch((error) => {
          if (!isCancel(error)) {
            self.failedToUpload.push(imageData.identifier);
            if (!error.response) {
              // network error
              self.pushCurrentGalleryError(`ไฟล์ภาพ ${imageData.title} ไม่สามารถอัปโหลดได้ในขณะนี้ โปรดลองอีกครั้ง`);
            } else {
              // http status code
              const code = error.response.status;
              self.pushCurrentGalleryError(`เกิดความผิดพลาดขณะอัปโหลดไฟล์ภาพ ${imageData.title} โปรดติดต่อผู้ดูแลระบบ (Error Code: ${code})`);
            }
          }
        });
    },
    retryUpload(identifier) {
      this.failedToUpload = _.remove(this.failedToUpload, identifier);
      const imagesToBeRetried = _.pull(this.images, (image) => {
        return image.identifier === identifier;
      });
      imagesToBeRetried.forEach((imageData) => {
        this.pushAndUploadImage(imageData.file);
      });
    },
    retryAll() {
      const failedImageData = _.forEach(this.failedToUpload, (identifier) => this.images.identifier === identifier);
      this.deleteAllFailedImages();
      failedImageData.forEach((imageData) => {
        this.pushAndUploadImage(imageData.file);
      });
    },
    abortUpload(identifier, imageIndex) {
      axiosImageAPI.cancel(identifier);
      this.$delete(this.images, imageIndex);
    },
    abortAll() {
      axiosImageAPI.cancelAll();
      this.deleteAllFailedImages();
    },
    deleteAllFailedImages() {
      this.images = _.remove(this.images, (item) => { return item.image; });
      this.resetCurrentGalleryErrors();
    },
    deleteImage(index, imageId) {
      if (this.deleteConfirm === index) {
        this.$delete(this.images, index);
        this.pushCurrentGalleryToBeDeleted(imageId);
        this.setCurrentGalleryWasEdited();
        this.uploadSuccessful -= 1;
        this.deleteConfirm = false;
      } else {
        this.deleteConfirm = index;
        setTimeout(() => {
          this.deleteConfirm = null;
        }, 1800);
      }
    },
    reorder() {
      const items = this.images;
      const itemIds = [];
      items.forEach((item) => {
        itemIds.push(item.id);
      });
      this.setCurrentGalleryWasReordered();
      this.setCurrentGallery({
        ...this.currentGallery,
        items,
        itemIds,
      });
    },
    setCoverImage(coverImage) {
      this.setCurrentGallery({
        ...this.currentGallery,
        coverImage: coverImage.image,
      });
    },
    clearErrors() {
      this.errors = [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/helpers.scss';
@import './src/assets/scss/themes/components/progress-bar.scss';

.alert-danger {
  position: relative;
  border-radius: 0.25rem;
  p {
    margin-top: 10px;
  }
  .btn-clear-error {
    cursor: pointer;
    position: absolute;
    top: 16px;
    right: 0;
  }
}
.uploader {
  width: 100%;
  background-clip: padding-box;
  background-color: #fff;
  border: 1px solid #dee2e6;
  color: #777776;
  padding: 40px 15px;
  text-align: center;
  border-radius: 0.25rem;
  font-size: 20px;
  position: relative;
  &.dragging {
    background: #fff;
    color: $altv-navy;
    border: 3px dashed $altv-navy;
    .file-input label {
      background: $altv-navy;
      color: #fff;
    }
  }
  i {
    font-size: 85px;
  }
  .file-input {
    width: 200px;
    margin: auto;
    height: 68px;
    position: relative;
    label,
    input {
      background: $altv-navy;
      color: #fff;
      width: 100%;
      position: absolute;
      left: 0;
      top: 0;
      padding: 10px;
      border-radius: 4px;
      margin-top: 7px;
      cursor: pointer;
      font-size: 16px;
    }
    .label-disabled {
      background: #adb5bd;
      border-color: #adb5bd;
      color: #777776;
      cursor: not-allowed;
    }
    input {
      opacity: 0;
      z-index: -2;
    }
  }
  .images-preview {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    margin-top: $spacer;
    justify-content: flex-start;
    align-items: flex-start;
    align-self: center;
    .img-wrapper {
      position: relative;
      flex: 0 0 23.45%;
      margin-left: $spacer / 2;
      margin-right: $spacer / 2;
      margin-bottom: $spacer;
      flex-direction: column;
      justify-content: flex-start;
      align-content: stretch;
      background: #fff;
      box-shadow: 1px 5px 20px rgba(62, 55, 55, 0.3);
      .image-wrapper-actions {
        position: absolute;
        width: 100%;
        height: 34px;
        /*background: rgba(0, 0, 0, 0.25);*/
        background: rgb(0, 0, 0);
        background: -moz-linear-gradient(
          180deg,
          rgba(0, 0, 0, 0.7) 0%,
          rgba(255, 255, 255, 0) 100%
        );
        background: -webkit-linear-gradient(
          180deg,
          rgba(0, 0, 0, 0.7) 0%,
          rgba(255, 255, 255, 0) 100%
        );
        background: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0.7) 0%,
          rgba(255, 255, 255, 0) 100%
        );
        filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#000000",endColorstr="#ffffff",GradientType=1);
        z-index: 3;
      }
      .display-image {
        position: relative;
        height: 200px;
        width: 100%;
        div {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
        }
        .img-container {
          background-position: 50% 50%;
          background-size: cover;
          cursor: move;
          z-index: 2;
          border-bottom: 1px solid $gray-200;
        }
        .loading-container {
          background-image: url('~@/assets/images/loading.gif');
          background-repeat: no-repeat;
          background-position: 50% 50%;
          background-size: 100px;
          z-index: 1;
        }
        .loading-container.upload-failed {
          background-image: url('~@/assets/images/uploadFailed.jpg');
          background-size: cover;
        }
      }
      .btn-delete {
        position: absolute;
        top: 2px;
        right: 4px !important;
        border: none;
        background: transparent;
        z-index: 3;
        i {
          font-size: 20px;
          color: #fff;
          transition: color 250ms ease;
        }
        &:hover i {
          color: $red;
        }
        &:focus {
          outline: none;
        }
      }
      .btn-set-cover {
        position: absolute;
        top: 0;
        left: 0;
        border: none;
        background: transparent;
        padding: 2px 2px 2px 7px;
        color: #fff;
        z-index: 4;
        span {
          display: none;
          font-size: 14px;
          margin-left: 2px;
          text-shadow: 1px 0 4px #000;
        }
        i {
          font-size: 18px;
          color: #fff;
          transition: color 250ms ease;
        }
        &:hover {
          i {
            color: $yellow;
          }
          span {
            display: inline;
          }
        }
        &:focus {
          outline: none;
        }
      }
      .btn-delete-confirm {
        position: absolute;
        top: 0;
        right: 0;
        border: none;
        background: $red;
        font-size: 12px;
        padding: 8px;
        color: #fff;
        z-index: 4;
        i {
          font-size: 12px;
          color: #fff;
        }
        &:focus {
          outline: none;
        }
      }
      img {
        cursor: move;
        max-height: 110px;
        object-fit: cover;
        object-position: center;
      }
    }
    .progress {
      .glowing {
        animation: move 5s cubic-bezier(0.93, 0.71, 0.39, 0.04) infinite;
      }
    }
    .details {
      font-size: 12px;
      background: #fff;
      color: $body-color;
      display: flex;
      flex-direction: column;
      align-items: self-start;
      padding: 3px 6px 6px;
      input {
        width: 100%;
        color: #495057;
      }
      label {
        font-size: 10px;
        margin-top: 10px;
        margin-bottom: 0;
      }
      .name {
        overflow: hidden;
        height: 24px;
        border: none;
        border-bottom: 1px solid #ccc;
        &:disabled {
          border-bottom: none;
        }
      }
      .desc {
        overflow: hidden;
        height: 44px;
        width: 100%;
        border: none;
        border-bottom: 1px solid #ccc;
      }
      .btn-retryUpload {
        width: 100%;
        i {
          font-size: 16px;
        }
      }
    }
  }
  .upload-control {
    position: absolute;
    width: 100%;
    background: #fff;
    top: 0;
    left: 0;
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
    padding: 10px 10px 4px;
    text-align: right;
  }
}
.progress {
  .glowing {
    animation: moveLong 5s cubic-bezier(0.93, 0.71, 0.39, 0.04) infinite;
  }
}
.alert {
  border-radius: 0.25rem;
  button {
    margin-right: 10px;
  }
}


@media (max-width: 575.98px) {
  .uploader {
    padding: $spacer * 2.5 0 0;
    .images-preview {
      display: flex;
      flex-wrap: wrap;
      margin-top: $spacer;
      justify-content: space-between;
      align-items: flex-start;
      padding: 4px;
      .img-wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 240px;
        justify-content: space-between;
        margin-left: 0;
        margin-right: 0;
        margin-bottom: 1rem;
        flex: 0 0 48.75%;
        background: #fff;
        box-shadow: 1px 5px 20px rgba(62, 55, 55, 0.3);
        .image-wrapper-actions {
          position: absolute;
          width: 100%;
          height: 34px;
        }
        .display-image {
          position: relative;
          height: 200px;
          width: 100%;
          div {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
          }
          .img-container {
            background-position: 50% 50%;
            background-size: cover;
            cursor: move;
            z-index: 2;
          }
          .loading-container {
            background-image: url('~@/assets/images/loading.gif');
            background-repeat: no-repeat;
            background-position: 50% 50%;
            background-size: 100px;
            z-index: 1;
          }
          .loading-container.upload-failed {
            background-image: url('~@/assets/images/uploadFailed.jpg');
            background-size: cover;
          }
        }
      }
    }
  }
}

@media (max-width: 991.98px) {
  .uploader {
    padding: $spacer * 2.5 0 0;
    .images-preview {
      display: flex;
      flex-wrap: wrap;
      margin-top: $spacer;
      justify-content: flex-start;
      align-items: flex-start;
      padding: 4px;
      .img-wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 240px;
        justify-content: space-between;
        margin-right: $spacer / 2;
        margin-bottom: $spacer / 2;
        flex: 0 0 30.5%;
        background: #fff;
        box-shadow: 1px 5px 20px rgba(62, 55, 55, 0.3);
        .image-wrapper-actions {
          position: absolute;
          width: 100%;
          height: 34px;
        }
        .display-image {
          position: relative;
          height: 200px;
          width: 100%;
          div {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
          }
          .img-container {
            background-position: 50% 50%;
            background-size: cover;
            cursor: move;
            z-index: 2;
          }
          .loading-container {
            background-image: url('~@/assets/images/loading.gif');
            background-repeat: no-repeat;
            background-position: 50% 50%;
            background-size: 100px;
            z-index: 1;
          }
          .loading-container.upload-failed {
            background-image: url('~@/assets/images/uploadFailed.jpg');
            background-size: cover;
          }
        }
      }
    }
  }
}
.modal.isEditingGallery {
  .uploader {
    @extend .scrollbar;
    max-height: 400px;
    overflow-y: auto;
  }
}
</style>
